<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div class="sm:flex sm:items-center sm:justify-between">
      <div>
        <Link
          :href="route('site-owner.invoices.index')"
          class="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300 mb-2"
        >
          <ArrowLeftIcon class="h-4 w-4 mr-1" />
          Back to Invoices
        </Link>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
          Invoice {{ invoice.invoice_number }}
        </h1>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Invoice details and payment information
        </p>
      </div>
      <div class="mt-4 sm:mt-0">
        <span
          :class="getStatusBadgeClass(invoice.status_color)"
          class="px-3 py-2 inline-flex text-sm leading-5 font-semibold rounded-lg"
        >
          {{ invoice.status_label }}
        </span>
      </div>
    </div>

    <!-- Invoice Summary -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <!-- Company Info -->
          <div>
            <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Company</h3>
            <p class="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
              {{ invoice.company.name }}
            </p>
            <Link
              :href="route('site-owner.companies.show', invoice.company.id)"
              class="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              View Company Details
            </Link>
          </div>

          <!-- Subscription Plan -->
          <div v-if="invoice.subscription">
            <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Subscription Plan</h3>
            <p class="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
              {{ invoice.subscription.plan_name }}
            </p>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Billing: {{ invoice.subscription.billing_period }}
            </p>
          </div>

          <!-- Invoice Dates -->
          <div>
            <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Dates</h3>
            <div class="mt-2 space-y-1">
              <p class="text-sm text-gray-900 dark:text-white">
                <span class="font-medium">Invoice Date:</span> {{ formatDate(invoice.invoice_date) }}
              </p>
              <p class="text-sm text-gray-900 dark:text-white">
                <span class="font-medium">Due Date:</span> {{ formatDate(invoice.due_date) }}
                <span v-if="invoice.is_overdue" class="ml-2 text-red-600 dark:text-red-400 font-semibold">
                  (Overdue)
                </span>
              </p>
              <p v-if="invoice.paid_at" class="text-sm text-gray-900 dark:text-white">
                <span class="font-medium">Paid At:</span> {{ formatDateTime(invoice.paid_at) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Line Items -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Line Items</h3>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Description
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Quantity
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Unit Price
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-for="(line, index) in invoice.lines" :key="index">
                <td class="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {{ line.description }}
                </td>
                <td class="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                  {{ line.quantity }}
                </td>
                <td class="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                  {{ invoice.currency === 'gbp' ? '£' : '$' }}{{ formatCurrency(line.unit_price) }}
                </td>
                <td class="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                  {{ invoice.currency === 'gbp' ? '£' : '$' }}{{ formatCurrency(line.amount) }}
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50 dark:bg-gray-900">
              <tr>
                <td colspan="3" class="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                  Subtotal:
                </td>
                <td class="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                  {{ invoice.currency === 'gbp' ? '£' : '$' }}{{ formatCurrency(invoice.subtotal) }}
                </td>
              </tr>
              <tr>
                <td colspan="3" class="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                  Tax:
                </td>
                <td class="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                  {{ invoice.currency === 'gbp' ? '£' : '$' }}{{ formatCurrency(invoice.tax_amount) }}
                </td>
              </tr>
              <tr>
                <td colspan="3" class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
                  Total:
                </td>
                <td class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
                  {{ invoice.currency === 'gbp' ? '£' : '$' }}{{ formatCurrency(invoice.total_amount) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Payment Information -->
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Payment Information</h3>

        <dl class="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
          <div>
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Method</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white">
              {{ invoice.payment_method || 'Not specified' }}
            </dd>
          </div>

          <div v-if="invoice.stripe_invoice_id">
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Stripe Invoice ID</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white font-mono">
              {{ invoice.stripe_invoice_id }}
            </dd>
          </div>

          <div v-if="invoice.stripe_payment_intent_id">
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Stripe Payment Intent</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white font-mono">
              {{ invoice.stripe_payment_intent_id }}
            </dd>
          </div>

          <div v-if="invoice.paid_at">
            <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Date</dt>
            <dd class="mt-1 text-sm text-gray-900 dark:text-white">
              {{ formatDateTime(invoice.paid_at) }}
            </dd>
          </div>
        </dl>
      </div>
    </div>

    <!-- Payment Transactions -->
    <div v-if="transactions.length > 0" class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Payment Transactions</h3>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Transaction ID
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Method
                </th>
                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-for="transaction in transactions" :key="transaction.id">
                <td class="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {{ formatDateTime(transaction.created_at) }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {{ transaction.type }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono">
                  {{ transaction.transaction_id }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {{ transaction.payment_method }}
                </td>
                <td class="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                  {{ invoice.currency === 'gbp' ? '£' : '$' }}{{ formatCurrency(transaction.amount) }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  <span
                    :class="[
                      transaction.status === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                      'px-2 inline-flex text-xs leading-5 font-semibold rounded-full'
                    ]"
                  >
                    {{ transaction.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Billing Address -->
    <div v-if="invoice.billing_address" class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Billing Address</h3>
        <div class="text-sm text-gray-900 dark:text-white whitespace-pre-line">
          {{ formatBillingAddress(invoice.billing_address) }}
        </div>
      </div>
    </div>

    <!-- Notes -->
    <div v-if="invoice.notes" class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Notes</h3>
        <div class="text-sm text-gray-900 dark:text-white whitespace-pre-line">
          {{ invoice.notes }}
        </div>
      </div>
    </div>

    <!-- Metadata -->
    <div v-if="invoice.metadata" class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="px-4 py-5 sm:p-6">
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Metadata</h3>
        <pre class="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-4 rounded overflow-x-auto">{{ JSON.stringify(invoice.metadata, null, 2) }}</pre>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import { ArrowLeftIcon } from '@heroicons/vue/24/outline';
import route from '@/Utils/route';

const props = defineProps({
  invoice: {
    type: Object,
    required: true,
  },
  transactions: {
    type: Array,
    default: () => [],
  },
});

const formatCurrency = (amount) => {
  if (!amount) return '0.00';
  return parseFloat(amount).toFixed(2);
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateTime = (datetime) => {
  if (!datetime) return '-';
  return new Date(datetime).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatBillingAddress = (address) => {
  if (typeof address === 'string') return address;
  if (typeof address === 'object') {
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.postal_code,
      address.country,
    ].filter(Boolean);
    return parts.join('\n');
  }
  return 'No address provided';
};

const getStatusBadgeClass = (color) => {
  const colorMap = {
    gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  };
  return colorMap[color] || colorMap.gray;
};
</script>
